/**
 * Lo que las pantallas de Reservas necesitan saber.
 *
 * Servicios y Citas miran la misma zona horaria, recursos, catálogo y reservas.
 * Dentro de Citas, calendario y lista son sólo dos vistas del mismo estado: no
 * vuelven a pedir los datos al cambiar entre ellas.
 *
 * El rango nace amplio a propósito: el calendario puede recorrer el historial y
 * todo el horizonte configurable de reservas (365 días como máximo), así que la
 * colección de citas tiene que cubrir lo mismo que la disponibilidad.
 */
import { useState } from 'react'
import { useSectionResource } from '../../../hooks/useSectionResource'

import { bookingError } from './bookingShared'
import {
  getBookingSchedule,
  listBookings,
  listOffers,
  type Booking,
  type BookingResource,
  type Offer,
} from '../../../lib/api'

/** Hoy en la zona del negocio. El calendario del panel es el suyo, no el mío. */
export function todayIn(timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date())
}

export function shiftDay(day: string, days: number): string {
  const [year, month, date] = day.split('-').map(Number)
  return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, (date ?? 1) + days)).toISOString().slice(0, 10)
}

export interface BookingsData {
  timezone: string
  resources: BookingResource[]
  setResources: (resources: BookingResource[]) => void
  /** Quién atiende con «Cualquiera disponible». */
  /** Vuelve a pedir el equipo y su horario (tras darle de alta a alguien). */
  reloadSchedule: () => Promise<void>
  /** Todo el catálogo, reservable o no. */
  offers: Offer[]
  /** Sólo lo reservable y vivo: es lo que llenan las tres pantallas. */
  services: Offer[]
  bookings: Booking[]
  range: { from: string; to: string } | null
  setRange: (range: { from: string; to: string }) => void
  /** Si el rango lo eligió alguien, y no es el de fábrica (un año a cada lado). */
  rangeIsCustom: boolean
  /** Vuelve al rango de fábrica: es «quitar el filtro de fechas». */
  clearRange: () => void
  refreshing: boolean
  refresh: () => void
  loading: boolean
  scheduleLoading: boolean
  failed: boolean
  failureReason: string
  setFailureReason: (reason: string) => void
  reloadOffers: () => void
  reloadBookings: () => void
  addOffer: (offer: Offer) => void
  applyOffer: (offer: Offer) => void
  applyBooking: (booking: Booking) => void
  removeOffer: (offerId: string) => void
}

export function useBookingsData(projectId: string, token: string): BookingsData {
  const scheduleQuery = useSectionResource(token, [projectId, 'schedule'], () => getBookingSchedule(projectId, token))
  const offersQuery = useSectionResource(token, [projectId, 'offers'], () => listOffers(projectId, token))
  const timezone = scheduleQuery.data?.timezone || 'UTC'
  const resources = scheduleQuery.data?.resources ?? []
  const offers = offersQuery.data ?? []
  const [customRange, setRange] = useState<{ from: string; to: string } | null>(null)
  const today = todayIn(timezone)
  const range = customRange ?? { from: shiftDay(today, -365), to: shiftDay(today, 365) }
  const bookingsQuery = useSectionResource(token, [projectId, 'bookings', range.from, range.to],
    () => listBookings(projectId, range, token), Boolean(scheduleQuery.data))
  const bookings = bookingsQuery.data ?? []
  const [localFailure, setFailureReason] = useState('')
  const error = scheduleQuery.error || offersQuery.error || bookingsQuery.error
  const failed = Boolean(error)
  const failureReason = error ? bookingError(error) : localFailure
  const loading = offersQuery.loading || bookingsQuery.loading
  const scheduleLoading = scheduleQuery.loading
  const setResources = (next: BookingResource[]) => {
    if (scheduleQuery.data) scheduleQuery.setData({ ...scheduleQuery.data, resources: next })
  }
  const setOffers = offersQuery.setData
  const setBookings = bookingsQuery.setData
  const refresh = () => {
    setFailureReason('')
    void scheduleQuery.refresh()
    void offersQuery.refresh()
    void bookingsQuery.refresh()
  }

  /* Reservable = tiene duración. Sin ella no hay calendario que enseñar, y
     archivado tampoco cuenta: sigue existiendo por sus cobros, no por su
     agenda. */
  const services = offers.filter(
    (offer) => offer.booking.durationMinutes !== null && offer.status !== 'archived',
  )

  return {
    timezone,
    resources,
    setResources,
    reloadSchedule: async () => { await scheduleQuery.refresh() },
    offers,
    services,
    bookings,
    range,
    setRange: (next) => setRange(next),
    rangeIsCustom: customRange !== null,
    clearRange: () => setRange(null),
    refreshing: scheduleQuery.refreshing || offersQuery.refreshing || bookingsQuery.refreshing,
    refresh,
    loading,
    scheduleLoading,
    failed,
    failureReason,
    setFailureReason,
    reloadOffers: () => { void offersQuery.refresh() },
    reloadBookings: () => { void bookingsQuery.refresh() },
    addOffer: (created) => setOffers((rows) => [created, ...(rows ?? [])]),
    applyOffer: (updated) => setOffers((rows) => (rows ?? []).map((row) => (row.id === updated.id ? updated : row))),
    applyBooking: (updated) => setBookings((rows) => (rows ?? []).map((row) => (row.id === updated.id ? updated : row))),
    removeOffer: (offerId) => setOffers((rows) => (rows ?? []).filter((row) => row.id !== offerId)),
  }
}
